import React from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import { ChevronRight, CheckCircle2, Phone, ArrowRight } from "lucide-react";
import { getSeoService, SEO_SERVICES, SERVICE_AREAS, SITE } from "../seo";
import { GALLERY, COMPANY } from "../mock";
import { SectionHeading, FAQAccordion } from "../components/common";
import { Button } from "../components/ui/button";
import useScrollReveal from "../hooks/useScrollReveal";
import useSEO from "../hooks/useSEO";
import { BeforeAfterSection } from "../components/BeforeAfterSlider";

export default function SeoService({ slug: fixedSlug }) {
  const params = useParams();
  const slug = fixedSlug || params.slug;
  const svc = getSeoService(slug);
  const navigate = useNavigate();
  useScrollReveal([slug]);
  useSEO({
    title: svc ? svc.metaTitle : "Services | Miller & Miller",
    description: svc ? svc.metaDescription : "",
    keywords: svc ? svc.keywords : [],
    path: svc ? svc.path : "/services",
    image: svc ? svc.image : undefined,
    jsonLd: svc
      ? {
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: svc.nav,
          provider: { "@type": "GeneralContractor", name: SITE.name, telephone: "+13216080255" },
          areaServed: SERVICE_AREAS.map((c) => c.full),
          name: svc.h1,
          description: svc.metaDescription,
        }
      : undefined,
  });

  if (!svc) return <Navigate to="/services" replace />;

  const strip = GALLERY.filter((g) => g.src !== svc.image).slice(0, 4);
  const others = SEO_SERVICES.filter((s) => s.slug !== svc.slug);

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-24 pb-10 md:pt-40 md:pb-24 bg-brand-navy text-white overflow-hidden">
        <img src={svc.image} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navydark/92 via-brand-navy/85 to-brand-ocean/50" />
        <div className="container-x relative">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-white/60 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/services" className="hover:text-white">Services</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-brand-gold2">{svc.nav}</span>
          </nav>
          <h1 className="text-3xl md:text-5xl font-heading font-extrabold max-w-3xl">{svc.h1}</h1>
          <p className="mt-4 text-white/80 text-lg max-w-2xl">{svc.intro}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button onClick={() => navigate("/estimate")} className="h-12 px-7 bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-bold shadow-gold">
              Get My Free Quote <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-white/10 hover:bg-white/20 text-white font-heading font-bold ring-1 ring-white/25 transition-colors">
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Overview + benefits */}
      <section className="section-y bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-12 items-start">
          <div className="reveal rounded-3xl overflow-hidden shadow-card lg:sticky lg:top-28">
            <img src={svc.image} alt={svc.h1} className="w-full h-[420px] object-cover" />
          </div>
          <div>
            <SectionHeading eyebrow={svc.eyebrow} title={`Why Choose Our ${svc.nav}`} />
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {svc.benefits.map((b, i) => (
                <div key={i} className="reveal rounded-2xl bg-brand-mist/50 p-5" style={{ transitionDelay: `${i * 50}ms` }}>
                  <CheckCircle2 className="h-6 w-6 text-brand-ocean" />
                  <h3 className="mt-2 font-heading font-extrabold text-brand-navy">{b.title}</h3>
                  <p className="mt-1 text-sm text-brand-slate leading-relaxed">{b.text}</p>
                </div>
              ))}
            </div>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3 reveal">
              {svc.features.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm font-semibold text-brand-navy">
                  <CheckCircle2 className="h-5 w-5 text-brand-ocean shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {svc.slug === "pool-screen-repair-rescreening" && <BeforeAfterSection className="pb-16 md:pb-24 bg-white" />}

      {/* Gallery strip */}
      <section className="section-y bg-brand-mist/40">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <SectionHeading eyebrow="Recent Work" title="See It In Action" />
            <Link to="/gallery" className="reveal inline-flex items-center gap-2 h-12 px-6 rounded-md bg-brand-ocean hover:bg-brand-sky text-white font-heading font-bold transition-colors shrink-0">
              View Full Gallery <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-8 md:mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {strip.map((g, i) => (
              <Link key={i} to="/gallery" className="reveal group overflow-hidden rounded-xl shadow-card" style={{ transitionDelay: `${i * 60}ms` }}>
                <img src={g.src} alt={g.title} loading="lazy" className="h-52 w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-y bg-white">
        <div className="container-x max-w-3xl">
          <SectionHeading center eyebrow="Questions?" title={`${svc.nav} FAQs`} />
          <div className="mt-8 md:mt-10"><FAQAccordion faqs={svc.faqs} /></div>
        </div>
      </section>

      {/* Related services */}
      <section className="section-y bg-brand-mist/40">
        <div className="container-x">
          <SectionHeading center eyebrow="Explore More" title="Related Services" />
          <div className="mt-8 md:mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {others.map((s, i) => (
              <Link key={s.slug} to={s.path} className="reveal group overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-brand-mist hover:ring-brand-sky/40 transition-all" style={{ transitionDelay: `${i * 50}ms` }}>
                <div className="h-36 overflow-hidden">
                  <img src={s.image} alt={s.nav} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-4">
                  <h3 className="font-heading font-extrabold text-brand-navy text-sm group-hover:text-brand-ocean transition-colors">{s.nav}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-brand-ocean to-brand-sky">
        <div className="container-x py-14 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-white">Ready for your {svc.nav.toLowerCase()}?</h2>
          <p className="text-white/90 mt-3">Serving Melbourne, FL and all of Brevard County with fast, free estimates.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button onClick={() => navigate("/estimate")} className="h-12 px-8 bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-extrabold shadow-gold">
              Get My Free Quote <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-brand-navy/30 hover:bg-brand-navy/50 text-white font-heading font-bold ring-1 ring-white/30 transition-colors">
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
